import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const JointCaseStudies = () => {
  const caseStudies = [
  {
    id: 1,
    title: "Healthcare Cloud Migration with Microsoft Azure",
    partner: "Microsoft",
    client: "MediCare Plus Hospital Network",
    industry: "Healthcare",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a7cf43e4-1764670836798.png",
    imageAlt: "Modern hospital building with glass facade and medical professionals walking through bright lobby with digital health technology displays",
    challenge: "Legacy on-premise infrastructure limiting scalability and patient data accessibility across 12 hospital locations.",
    solution: "Implemented Azure cloud infrastructure with Microsoft 365 integration, enabling secure patient data management and real-time collaboration.",
    results: [
    { metric: "40%", label: "Cost Reduction" },
    { metric: "99.9%", label: "Uptime Achieved" },
    { metric: "3x", label: "Faster Data Access" }],

    technologies: ["Azure Cloud", "Microsoft 365", "Azure Security Center"],
    duration: "6 months",
    icon: "Cloud"
  },
  {
    id: 2,
    title: "Enterprise CRM Transformation with Zoho",
    partner: "Zoho",
    client: "TechVision Solutions Pvt Ltd",
    industry: "Technology Services",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a0b7563b-1765794380085.png",
    imageAlt: "Business team collaborating around conference table with laptops and digital screens showing CRM dashboards and sales analytics",
    challenge: "Fragmented customer data across multiple systems causing sales inefficiency and poor customer experience.",
    solution: "Deployed Zoho CRM with custom workflows, analytics dashboards, and integration with existing business applications.",
    results: [
    { metric: "65%", label: "Sales Productivity Increase" },
    { metric: "50%", label: "Faster Lead Response" },
    { metric: "85%", label: "Customer Satisfaction" }],

    technologies: ["Zoho CRM", "Zoho Analytics", "Zoho Flow"],
    duration: "4 months",
    icon: "Layers"
  },
  {
    id: 3,
    title: "Cybersecurity Infrastructure with Acronis",
    partner: "Acronis",
    client: "SecureBank Financial Services",
    industry: "Banking & Finance",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f3787661-1764669639800.png",
    imageAlt: "Modern banking office with security operations center featuring multiple monitors displaying cybersecurity dashboards and threat detection systems",
    challenge: "Increasing cyber threats and regulatory compliance requirements demanding enterprise-grade security and backup solutions.",
    solution: "Implemented Acronis Cyber Protect with automated backup, disaster recovery, and real-time threat intelligence monitoring.",
    results: [
    { metric: "100%", label: "Data Protection" },
    { metric: "15min", label: "Recovery Time" },
    { metric: "Zero", label: "Security Breaches" }],

    technologies: ["Acronis Cyber Protect", "Backup Cloud", "Threat Intelligence"],
    duration: "5 months",
    icon: "Shield"
  }];


  return (
    <section className="py-12 md:py-16 lg:py-20 px-4 md:px-6 lg:px-8 bg-muted/30">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12 md:mb-16 lg:mb-20">
          <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
            <Icon name="Briefcase" size={18} color="var(--color-primary)" />
            <span className="text-primary text-sm font-medium">Success Stories</span>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Joint Case Studies & Implementations
          </h2>
          <p className="text-text-secondary text-sm md:text-base lg:text-lg max-w-3xl mx-auto">
            Real-world success stories showcasing collaborative solutions with our technology partners delivering measurable business outcomes and digital transformation excellence.
          </p>
        </div>

        <div className="space-y-8 md:space-y-12 lg:space-y-16">
          {caseStudies?.map((study, index) =>
          <div
            key={study?.id}
            className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-6 md:gap-8 lg:gap-12 items-center`}>

              <div className="w-full lg:w-1/2">
                <div className="relative rounded-2xl overflow-hidden shadow-lg group">
                  <div className="aspect-[4/3] overflow-hidden">
                    <Image
                    src={study?.image}
                    alt={study?.imageAlt}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />

                  </div>
                  <div className="absolute top-4 left-4 bg-white rounded-full px-4 py-2 shadow-md flex items-center gap-2">
                    <Icon name={study?.icon} size={18} color="var(--color-primary)" />
                    <span className="text-sm font-semibold text-foreground">{study?.partner}</span>
                  </div>
                </div>
              </div>

              <div className="w-full lg:w-1/2">
                <div className="bg-card rounded-2xl p-6 md:p-8 shadow-md">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="px-3 py-1 bg-secondary/10 text-secondary rounded-full text-xs font-medium">
                      {study?.industry}
                    </span>
                    <span className="px-3 py-1 bg-muted text-text-secondary rounded-full text-xs font-medium">
                      {study?.duration}
                    </span>
                  </div>

                  <h3 className="text-xl md:text-2xl font-bold text-foreground mb-3">
                    {study?.title}
                  </h3>

                  <div className="flex items-center gap-2 mb-6">
                    <Icon name="Building2" size={16} color="var(--color-text-secondary)" />
                    <span className="text-sm text-text-secondary">{study?.client}</span>
                  </div>

                  <div className="space-y-4 mb-6">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Icon name="AlertCircle" size={16} color="var(--color-destructive)" />
                        <span className="text-sm font-semibold text-foreground">Challenge</span>
                      </div>
                      <p className="text-sm text-text-secondary pl-6">{study?.challenge}</p>
                    </div>

                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Icon name="Lightbulb" size={16} color="var(--color-accent)" />
                        <span className="text-sm font-semibold text-foreground">Solution</span>
                      </div>
                      <p className="text-sm text-text-secondary pl-6">{study?.solution}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4 mb-6">
                    {study?.results?.map((result, resultIndex) =>
                  <div key={resultIndex} className="text-center p-3 bg-muted/50 rounded-lg">
                        <div className="text-xl md:text-2xl font-bold text-primary mb-1">{result?.metric}</div>
                        <div className="text-xs text-text-secondary">{result?.label}</div>
                      </div>
                  )}
                  </div>

                  <div className="border-t border-border pt-4">
                    <div className="text-xs font-semibold text-foreground mb-3">Technologies Used</div>
                    <div className="flex flex-wrap gap-2">
                      {study?.technologies?.map((tech, techIndex) =>
                    <span
                      key={techIndex}
                      className="px-3 py-1 bg-primary/10 text-primary rounded-full text-xs">

                          {tech}
                        </span>
                    )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-12 md:mt-16 text-center">
          <div className="inline-flex items-center gap-2 bg-accent/10 px-6 py-3 rounded-full">
            <Icon name="TrendingUp" size={20} color="var(--color-accent)" />
            <span className="text-sm font-medium text-accent">
              Average 50% improvement in operational efficiency across all implementations
            </span>
          </div>
        </div>
      </div>
    </section>);

};

export default JointCaseStudies;